import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Supported Living', path: '/supported-living' },
    { name: 'Staffing', path: '/staffing' },
    { name: 'Recruitment', path: '/recruitment' },
    { name: 'About', path: '/about' }
  ];

  const complianceLinks = [
    { name: 'Compliance Overview', path: '/compliance' },
    { name: 'Safeguarding Adults', path: '/compliance/safeguarding-adults' },
    { name: 'Complaints & Compliments', path: '/compliance/complaints' },
    { name: 'Duty of Candour', path: '/compliance/duty-of-candour' },
    { name: 'Privacy Notice', path: '/compliance/privacy-notice' },
    { name: 'Equality & Diversity', path: '/compliance/equality-diversity' },
    { name: 'Governance & Quality', path: '/compliance/governance-quality' },
    { name: 'CQC Status', path: '/compliance/cqc-status' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-md'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/logo.jpg" 
              alt="Three Spheres Healthcare" 
              className="h-16 w-auto md:h-20"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-[#4A90E2] ${
                  location.pathname === link.path 
                    ? 'text-[#4A90E2]' 
                    : 'text-gray-700'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Compliance Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-[#4A90E2] transition-colors">
                Compliance
                <ChevronDown size={16} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {complianceLinks.map((link) => (
                  <DropdownMenuItem key={link.path} asChild>
                    <Link 
                      to={link.path}
                      className="w-full cursor-pointer"
                    >
                      {link.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/contact"
              className={`text-sm font-medium transition-colors hover:text-[#4A90E2] ${
                location.pathname === '/contact' 
                  ? 'text-[#4A90E2]' 
                  : 'text-gray-700'
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href="tel:03300433693" 
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-[#4A90E2] transition-colors"
            >
              <Phone size={18} />
              <span>0330 043 3693</span>
            </a>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] hover:shadow-lg transition-all">
                Get in Touch
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-sm font-medium transition-colors hover:text-[#4A90E2] ${
                    location.pathname === link.path 
                      ? 'text-[#4A90E2]' 
                      : 'text-gray-700'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Compliance Submenu */}
              <div className="border-t pt-4">
                <p className="text-xs font-semibold text-gray-500 mb-3">COMPLIANCE</p>
                {complianceLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block py-2 text-sm font-medium transition-colors hover:text-[#4A90E2] ${
                      location.pathname === link.path 
                        ? 'text-[#4A90E2]' 
                        : 'text-gray-700'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>

              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-sm font-medium transition-colors hover:text-[#4A90E2] ${
                  location.pathname === '/contact' 
                    ? 'text-[#4A90E2]' 
                    : 'text-gray-700'
                }`}
              >
                Contact
              </Link>
              
              <div className="flex flex-col gap-3 mt-4 pt-4 border-t">
                <a 
                  href="tel:03300433693" 
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <Phone size={18} />
                  <span>0330 043 3693</span>
                </a>
                <a 
                  href="mailto:info@threesphereshealthcare.org" 
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <Mail size={18} />
                  <span>info@threesphereshealthcare.org</span>
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
